import React from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import PropTypes from 'prop-types';

import AudioPlayer from '../components/AudioPlayer';
import Layout from '../components/Layout';
import Container from '../components/Container';
import Notification from '../components/Notification';
import TextContent from '../components/TextContent';
import Section from '../components/Section';
import FooterBar from '../components/FooterBar';
import Grid from '../components/Grid';
import GridItem from '../components/GridItem';
import Table from '../components/Table';

import { GET_ACTIVITY_DETAILS } from '../graph/activities';
import { GET_CMS_CONTENT } from '../graph/cms';
import { GET_GOALS, ADD_GOAL, DELETE_GOAL } from '../graph/goals';

import extractGraphQLError from '../utils/extractGraphQLError';
import {
  extractPrismicString,
  extractPrismicContent,
  hasContent,
} from '../utils/prismic';
import { validate, requiredString } from '../utils/validate';
import useTrackActivityGA from '../utils/useTrackActivityGA';

import { activities, ACTIVITY_STATUS } from '../config/levels';

import { ReactComponent as RemoveIcon } from '../assets/remove-icon.svg';

const goalFormValidationSchema = validate({
  title: requiredString,
  description1: requiredString,
});

const goalFormInitialValues = {
  title: '',
  description1: '',
};

const goalFormFields = [
  {
    id: 'title',
    name: 'title',
    label: 'Sentence',
    type: 'select',
    defaultLabel: 'Select a sentence',
    options: [
      {
        id: '1',
        value: 'I care about...',
      },
      {
        id: '2',
        value: 'I like to...',
      },
      {
        id: '3',
        value: "I'm interested in...",
      },
      {
        id: '4',
        value: "I'm great at...",
      },
      {
        id: '5',
        value: 'I hate...',
      },
      {
        id: '6',
        value: 'Other',
      },
    ],
  },
  {
    id: 'description1',
    name: 'description1',
    label: 'Response',
    type: 'textarea',
    placeholder: 'Enter your response...',
  },
];

function ActivityGoals({ activityId }) {
  const { data: cmsData } = useQuery(GET_CMS_CONTENT, {
    fetchPolicy: 'cache-only',
  });

  const { data, loading } = useQuery(GET_ACTIVITY_DETAILS, {
    variables: { id: activityId },
  });

  const { data: goalData, loading: loadingGoals } = useQuery(GET_GOALS, {
    variables: { activityId },
  });

  const filteredGoals = goalData?.goals?.filter(goal => {
    return goal.type === 'INITIAL';
  });

  const isGoalsValid = filteredGoals?.length > 4;

  const [
    handleAddGoal,
    { loading: loadingAddGoal, error: errorAddGoal },
  ] = useMutation(ADD_GOAL, {
    refetchQueries: ['Goals'],
  });

  const handleNewGoal = formData => {
    handleAddGoal({
      variables: {
        activityId,
        type: 'INITIAL',
        ...formData,
      },
    });
  };

  const [
    handleDeleteGoal,
    { loading: loadingDeleteGoal, error: errorDeleteGoal },
  ] = useMutation(DELETE_GOAL, {
    refetchQueries: ['Goals'],
  });

  const activityType = data?.activity?.type;

  useTrackActivityGA(data?.activity?.type, '1');

  const { level } = activities[activityType] || {};

  const {
    title,
    sub_title: subTitle,
    instructions,
    audio_instructions_1: audioInstructions1,
    help_text_1: helpText1,
    short_instructions_1: shortInstructions1,
    step_label_1: stepInstructions1,
  } =
    cmsData?.cmsContent?.activity?.find(
      activity =>
        activity.id ===
        (cmsData?.user?.currentLevel === 4 &&
        data?.activity?.status === ACTIVITY_STATUS.COMPLETED
          ? `ongoing_${activityType}`
          : activityType),
    ) ||
    cmsData?.cmsContent?.activity?.find(
      activity => activity.id === activityType,
    ) ||
    {};

  return (
    <Layout
      title={`${extractPrismicString(title)}`}
      loading={loading || loadingGoals}
      hasFooterBar
    >
      <Container>
        <Section>
          {!!(hasContent(shortInstructions1) || hasContent(helpText1)) && (
            <Grid>
              <GridItem width={60}>
                <TextContent redh6 smallMargin>
                  {extractPrismicContent(shortInstructions1)}
                </TextContent>
              </GridItem>

              <GridItem width={40}>
                {!!(audioInstructions1?.url || hasContent(helpText1)) && (
                  <Notification showUser noMarginBottom>
                    {extractPrismicContent(helpText1)}

                    {!!audioInstructions1?.url && (
                      <AudioPlayer fileUrl={audioInstructions1?.url} />
                    )}
                  </Notification>
                )}
              </GridItem>
            </Grid>
          )}
        </Section>

        <TextContent redh6 smallMargin>
          <h6>Step 1</h6>
          <p>{extractPrismicString(stepInstructions1)}</p>
        </TextContent>

        <Table
          cols={[
            { id: 'title', value: 'Sentence' },
            { id: 'description1', value: 'Response' },
          ]}
          data={filteredGoals}
          redorderId="goalId"
          actions={[
            {
              id: 'remove',
              text: 'remove',
              icon: <RemoveIcon />,
              onClick: handleDeleteGoal,
              loading: loadingDeleteGoal,
            },
          ]}
          form={{
            fields: goalFormFields,
            loading: loadingAddGoal,
            validationSchema: goalFormValidationSchema,
            initialValues: goalFormInitialValues,
            onSubmit: handleNewGoal,
          }}
        />

        {!!(errorAddGoal || errorDeleteGoal) && (
          <Notification type="red">
            {extractGraphQLError(errorAddGoal || errorDeleteGoal)}
          </Notification>
        )}
      </Container>

      <FooterBar
        title={extractPrismicString(title)}
        subTitle={extractPrismicString(subTitle)}
        primaryButton={{
          label: 'Next: Define Goals',
          to: `/activities/${activityId}/goals/2`,
          disabled: !isGoalsValid,
          level,
        }}
        secondaryButton={{
          label: hasContent(instructions)
            ? 'Back to instructions'
            : 'Back to introduction',
          to: `/activities/${activityId}/${
            hasContent(instructions) ? 'instructions' : 'introduction'
          }`,
        }}
      />
    </Layout>
  );
}

ActivityGoals.propTypes = {
  activityId: PropTypes.string,
};

ActivityGoals.defaultProps = {
  activityId: null,
};

export default ActivityGoals;
